// CustomLists.js — Custom breakpoints management for organization admins.
//
// Slice 3: real data wired up.
//   - Eager-fetches amrie_antibiotics, amrie_organisms, species_master,
//     amrie_breakpoints, and the org's existing custom_breakpoints on mount
//   - Antibiotic dropdown: searchable Autocomplete from amrie_antibiotics
//     (selection-only, no free text)
//   - Potency dropdown: cascades from selected antibiotic (distinct potencies
//     in amrie_breakpoints for that drug)
//   - Organism dropdown: searchable Autocomplete from amrie_organisms
//     (selection-only). Species level only for v1; family/genus-level custom
//     rules are deferred (see revisit list).
//   - Host dropdown: from species_master, sorted Human-first then alphabetical
//     (no Human/Vet track filter — single combined list)
//   - Clinical indication dropdown: derived from amrie_breakpoints for the
//     selected organism+host, with "General / Not specified" as the no-site
//     option (matches the app's MetadataScreen labelling exactly)
//   - S and R numeric inputs with strict S > R validation
//   - Save writes to organizations/{orgId}/custom_breakpoints with the
//     canonical camelCase field shape
//   - Existing-breakpoints table shows R ≤ / I / S ≥ columns; I-range is
//     derived (R+1..S-1) for display, with "-" when there's no I zone
//     (S = R+1)
//
// Persisted document shape (must match Android OrgCustomBreakpoint):
//   {
//     whonetAbxCode, potency, organismCode, organismCodeType, host,
//     siteOfInfection, s, r, sourceLabel, rationale, active, createdBy,
//     createdAt
//   }

import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import {
  getAllOrganizations,
  getOrganizationDetails,
  getAmrieAntibiotics,
  getAmrieOrganisms,
  getSpeciesMaster,
  getAmrieBreakpoints,
  getCustomBreakpoints,
  addCustomBreakpoint,
  setCustomBreakpointActive,
  getClinicalIndications,
} from "./firestoreService";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AmrieBreakpointBrowser from "./AmrieBreakpointBrowser";

// "Any clinical indication" sentinel for the form. Persisted as null on
// the document. Matches the MetadataScreen "General / Not specified" label
// the lab tech sees during data entry.
const ANY_INDICATION_LABEL = "General / Not specified";

// Validation bounds — sanity check on zone diameter input.
// Disk itself is 6mm; zones rarely exceed 50mm.
const MIN_ZONE_MM = 6;
const MAX_ZONE_MM = 60;

/**
 * Derive the displayed I-range string for a custom breakpoint row.
 * - "-" if S = R + 1 (no integer falls strictly between R and S)
 * - single integer if R+1 = S-1
 * - "lo-hi" otherwise
 */
function formatIRange(r, s) {
  if (r == null || s == null) return "-";
  const iLow = r + 1;
  const iHigh = s - 1;
  if (iLow > iHigh) return "-";
  if (iLow === iHigh) return String(iLow);
  return `${iLow}-${iHigh}`;
}

export default function CustomLists() {
  const { currentUser, userRole } = useAuth();
  const { t, i18n } = useTranslation();
  const isSuperuser = userRole === "superuser";

  // --- Loading / error state ---
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // --- Master data (loaded once on mount, cached for session) ---
  const [antibiotics, setAntibiotics] = useState([]);
  const [organisms, setOrganisms] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [breakpoints, setBreakpoints] = useState([]);
  const [organizations, setOrganizations] = useState([]); // superuser only
  // clinical_indication_master raw array. The lookup map is derived via
  // useMemo so it rebuilds on language change without refetching.
  const [allClinicalIndications, setAllClinicalIndications] = useState([]);

  // --- Per-org state ---
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [orgDetails, setOrgDetails] = useState(null);
  const [customBreakpoints, setCustomBreakpoints] = useState([]);
  const [reloadCustomTrigger, setReloadCustomTrigger] = useState(0);

  // --- Dialog / form state ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [formAntibiotic, setFormAntibiotic] = useState(null);
  const [formPotency, setFormPotency] = useState("");
  const [formOrganism, setFormOrganism] = useState(null);
  const [formHost, setFormHost] = useState("");
  const [formSite, setFormSite] = useState(ANY_INDICATION_LABEL);
  const [formS, setFormS] = useState("");
  const [formR, setFormR] = useState("");
  const [formNotes, setFormNotes] = useState("");

  // ------------------------------------------------------------------------
  // Load master data + (for org admins) determine their org id automatically
  // ------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function loadMasterData() {
      setLoading(true);
      setLoadError(null);
      try {
        const [abx, orgs, sp, bps, allOrgs, indications] = await Promise.all([
          getAmrieAntibiotics(),
          getAmrieOrganisms(),
          getSpeciesMaster(),
          getAmrieBreakpoints(),
          isSuperuser ? getAllOrganizations() : Promise.resolve([]),
          getClinicalIndications(),
        ]);
        if (cancelled) return;
        setAntibiotics(abx);
        setOrganisms(orgs);
        setHosts(sp);
        setBreakpoints(bps);
        setOrganizations(allOrgs);
        setAllClinicalIndications(indications || []);

        // Org admins: auto-select their own org. Superusers must choose.
        if (!isSuperuser && currentUser) {
          const myOrgId = currentUser.organizationId;
          if (myOrgId) {
            setSelectedOrgId(myOrgId);
          }
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadMasterData();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isSuperuser]);

  // ------------------------------------------------------------------------
  // Load per-org data when selectedOrgId changes (or after a save)
  // ------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function loadOrgData() {
      if (!selectedOrgId) {
        setOrgDetails(null);
        setCustomBreakpoints([]);
        return;
      }
      try {
        const [details, customs] = await Promise.all([
          getOrganizationDetails(selectedOrgId),
          getCustomBreakpoints(selectedOrgId),
        ]);
        if (cancelled) return;
        setOrgDetails(details);
        // Show inactive rules too, so admins can re-enable them. The
        // table renders inactive rows with muted styling + strikethrough,
        // and the toggle action button flips between Disable and Enable.
        // Note: the cascade in MetadataScreen / EditTest already filters
        // inactive customs out (active === false check), so showing them
        // here doesn't risk leaking them into the user-facing dropdown.
        setCustomBreakpoints(customs);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || String(err));
      }
    }
    loadOrgData();
    return () => {
      cancelled = true;
    };
  }, [selectedOrgId, reloadCustomTrigger]);

  // ------------------------------------------------------------------------
  // Cascading dropdown derivations
  // ------------------------------------------------------------------------

  // Potencies for the selected antibiotic. Sourced from the antibiotic
  // document's POTENCIES field — the FULL list of disk strengths the
  // drug ships in (e.g. ["10µg", "30µg", "80µg"] for tetracycline).
  // Distinct from breakpoint potencies, which would only include
  // strengths AMRIE has a published rule for. Defining a custom rule
  // for a strength AMRIE doesn't yet cover is exactly the use case
  // for this UI, so the source must be the antibiotic doc.
  // Single source of truth shared with editCascade.js
  // (getPotenciesForAntibiotic) and Android's AssessDiskScreen.
  // Derived localization map. Keyed by display_name (English) — the lookup
  // key for both bp.siteOfInfection on existing breakpoints and the form's
  // current value. Rebuilds on language change without refetching.
  const clinicalIndicationMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const ci of allClinicalIndications) {
      if (ci?.displayName) {
        m[ci.displayName] = ci.translations?.[lang] || ci.displayName;
      }
    }
    return m;
  }, [allClinicalIndications, i18n.language]);

  const potencyOptions = useMemo(() => {
    if (!formAntibiotic) return [];
    // Source from the antibiotic's full potency list (every disk strength
    // available for the drug), not from breakpoints (which would only show
    // potencies that already have a published rule — defeating the point
    // of a custom breakpoint UI).
    if (!Array.isArray(formAntibiotic.potencies)) return [];
    return formAntibiotic.potencies;
  }, [formAntibiotic]);

  // Clinical indications — show all distinct sites globally from amrie_breakpoints,
  // plus "General / Not specified" at top. NOT cascaded by organism+host because
  // the whole point of custom rules is to define breakpoints for combinations
  // AMRIE doesn't currently cover.
  const indicationOptions = useMemo(() => {
    const set = new Set();
    for (const bp of breakpoints) {
      if (bp.siteOfInfection && bp.siteOfInfection.trim()) {
        set.add(bp.siteOfInfection);
      }
    }
    return [ANY_INDICATION_LABEL, ...Array.from(set).sort()];
  }, [breakpoints]);

  // ------------------------------------------------------------------------
  // Validation
  // ------------------------------------------------------------------------
  const validation = useMemo(() => {
    const errs = {};
    if (!formAntibiotic) errs.antibiotic = t("customLists.validationRequired");
    if (!formPotency) errs.potency = t("customLists.validationRequired");
    if (!formOrganism) errs.organism = t("customLists.validationRequired");
    if (!formHost) errs.host = t("customLists.validationRequired");
    // formSite has a default of ANY_INDICATION_LABEL so always valid

    const sNum = parseInt(formS, 10);
    const rNum = parseInt(formR, 10);
    if (!formS || isNaN(sNum)) {
      errs.s = t("customLists.validationRequired");
    } else if (sNum < MIN_ZONE_MM || sNum > MAX_ZONE_MM) {
      errs.s = t("customLists.validationOutOfRange", {
        min: MIN_ZONE_MM,
        max: MAX_ZONE_MM,
      });
    }
    if (!formR || isNaN(rNum)) {
      errs.r = t("customLists.validationRequired");
    } else if (rNum < MIN_ZONE_MM || rNum > MAX_ZONE_MM) {
      errs.r = t("customLists.validationOutOfRange", {
        min: MIN_ZONE_MM,
        max: MAX_ZONE_MM,
      });
    }
    if (!isNaN(sNum) && !isNaN(rNum) && sNum <= rNum) {
      errs.s = t("customLists.validationSGtR");
      errs.r = t("customLists.validationSGtR");
    }

    return { errs, isValid: Object.keys(errs).length === 0 };
  }, [formAntibiotic, formPotency, formOrganism, formHost, formS, formR, t]);

  // ------------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------------
  const handleOpenDialog = () => {
    setSaveError(null);
    setFormAntibiotic(null);
    setFormPotency("");
    setFormOrganism(null);
    setFormHost("");
    setFormSite(ANY_INDICATION_LABEL);
    setFormS("");
    setFormR("");
    setFormNotes("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) return; // don't close mid-save
    setDialogOpen(false);
  };

  const handleSave = async () => {
    if (!validation.isValid || !selectedOrgId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const sourceLabel = orgDetails?.shortName || orgDetails?.name || "";
      const data = {
        whonetAbxCode: formAntibiotic.whonetAbxCode,
        potency: formPotency,
        organismCode: formOrganism.whonetOrgCode,
        organismCodeType: "WHONET_ORG_CODE", // species level only for v1
        host: formHost,
        siteOfInfection: formSite === ANY_INDICATION_LABEL ? null : formSite,
        s: parseInt(formS, 10),
        r: parseInt(formR, 10),
        sourceLabel,
        rationale: formNotes || null,
        // active, createdBy, createdAt set by firestoreService
      };
      await addCustomBreakpoint(selectedOrgId, data);
      setDialogOpen(false);
      setReloadCustomTrigger((n) => n + 1);
    } catch (err) {
      setSaveError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  // Toggle a custom breakpoint between active and inactive. Inactive
  // rules are preserved on the document (so the audit trail isn't lost)
  // but skipped by the cascade and the interpretation matcher.
  const handleToggleActive = async (docId, currentlyActive) => {
    const verb = currentlyActive ? "Disable" : "Enable";
    const followup = currentlyActive
      ? "It will stop being applied to new tests but the audit record is preserved."
      : "It will be applied to new tests again.";
    if (!window.confirm(`${verb} this custom breakpoint? ${followup}`)) {
      return;
    }
    try {
      await setCustomBreakpointActive(
        selectedOrgId,
        docId,
        !currentlyActive,
        currentUser?.uid,
      );
      setReloadCustomTrigger((n) => n + 1);
    } catch (err) {
      alert(`Failed to ${verb.toLowerCase()}: ${err.message || err}`);
    }
  };

  // ------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 5, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>
          {t("customLists.loadingReferenceData")}
        </Typography>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">
          {t("customLists.loadFailedPrefix")} {loadError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t("customLists.pageTitle")}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: "900px" }}
      >
        {t("customLists.pageDescription")}
      </Typography>

      {/* AMRIE coverage browser — read-only, org-agnostic. Lets the
          admin survey what AMRIE already covers BEFORE defining a
          custom rule. Collapsed by default. */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="h6">
              {t("customLists.amrieBrowserTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("customLists.amrieBrowserCaption")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <AmrieBreakpointBrowser
            breakpoints={breakpoints}
            antibiotics={antibiotics}
          />
        </AccordionDetails>
      </Accordion>

      {/* Superuser-only: org selector */}
      {isSuperuser && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("customLists.organizationSuperuser")}</InputLabel>
            <Select
              value={selectedOrgId}
              label={t("customLists.organizationSuperuser")}
              onChange={(e) => setSelectedOrgId(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("customLists.selectOrganization")}</em>
              </MenuItem>
              {organizations.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name} {o.shortName ? `(${o.shortName})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* If no org selected (superuser before pick, or admin without one), prompt */}
      {!selectedOrgId ? (
        <Alert severity="info">
          {isSuperuser
            ? t("customLists.selectOrgPrompt")
            : t("customLists.noOrgPrompt")}
        </Alert>
      ) : (
        <>
          {/* Org context banner */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
            <Typography variant="body2" color="text.secondary">
              {t("customLists.managingFor")}{" "}
              <strong>{orgDetails?.name || selectedOrgId}</strong>
              {orgDetails?.shortName ? ` (${orgDetails.shortName})` : ""}
              {orgDetails?.primaryGuideline && (
                <>
                  {" "}
                  · {t("customLists.primaryGuidelineLabel")}{" "}
                  <strong>{orgDetails.primaryGuideline}</strong>
                </>
              )}
            </Typography>
          </Paper>

          {/* Existing breakpoints table */}
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {t("customLists.existingHeader", {
                  count: customBreakpoints.length,
                })}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
              >
                {t("customLists.addCustomBreakpoint")}
              </Button>
            </Box>

            {customBreakpoints.length === 0 ? (
              <Alert severity="info">{t("customLists.noBreakpointsYet")}</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("customLists.antibiotic")}</TableCell>
                      <TableCell>{t("customLists.tablePotency")}</TableCell>
                      <TableCell>{t("customLists.organism")}</TableCell>
                      <TableCell>{t("customLists.host")}</TableCell>
                      <TableCell>{t("customLists.indication")}</TableCell>
                      <TableCell align="right">R ≤</TableCell>
                      <TableCell align="center">I</TableCell>
                      <TableCell align="right">S ≥</TableCell>
                      <TableCell>{t("customLists.tableSourceLabel")}</TableCell>
                      <TableCell>{t("customLists.tableNotes")}</TableCell>
                      <TableCell align="center">
                        {t("customLists.tableActions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customBreakpoints.map((bp) => {
                      const isActive = bp.active !== false;
                      // Style inactive rows muted + strikethrough so the
                      // visual state matches the action they offer.
                      const rowSx = isActive
                        ? {}
                        : {
                            opacity: 0.55,
                            textDecoration: "line-through",
                            bgcolor: "action.hover",
                          };
                      return (
                        <TableRow key={bp.id} sx={rowSx}>
                          <TableCell>{bp.whonetAbxCode}</TableCell>
                          <TableCell>{bp.potency || "-"}</TableCell>
                          <TableCell>{bp.organismCode}</TableCell>
                          <TableCell>
                            {bp.host
                              ? hosts.find(
                                  (h) =>
                                    h.displayName === bp.host ||
                                    h.code === bp.host,
                                )?.translations?.[i18n.language] || bp.host
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {clinicalIndicationMap[
                              bp.siteOfInfection || ANY_INDICATION_LABEL
                            ] ||
                              bp.siteOfInfection ||
                              ANY_INDICATION_LABEL}
                          </TableCell>
                          <TableCell align="right">{bp.r ?? "-"}</TableCell>
                          <TableCell align="center">
                            {formatIRange(bp.r, bp.s)}
                          </TableCell>
                          <TableCell align="right">{bp.s ?? "-"}</TableCell>
                          <TableCell>{bp.sourceLabel}</TableCell>
                          <TableCell>
                            <Tooltip title={bp.rationale || ""}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 200 }}
                              >
                                {bp.rationale || "-"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                isActive
                                  ? t("customLists.tooltipDisable")
                                  : t("customLists.tooltipEnable")
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleToggleActive(bp.id, isActive)
                                }
                              >
                                {isActive ? (
                                  <BlockIcon fontSize="small" />
                                ) : (
                                  <RestartAltIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Add-breakpoint modal */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("customLists.addCustomBreakpoint")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            {/* Antibiotic — searchable, selection-only */}
            <Autocomplete
              options={antibiotics}
              getOptionLabel={(opt) =>
                `${opt.antibiotic || opt.whonetAbxCode} (${opt.whonetAbxCode})`
              }
              isOptionEqualToValue={(opt, val) =>
                opt.whonetAbxCode === val?.whonetAbxCode
              }
              value={formAntibiotic}
              onChange={(_, newValue) => {
                setFormAntibiotic(newValue);
                setFormPotency(""); // cascade reset
              }}
              autoSelect={false}
              autoHighlight
              clearOnBlur
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("customLists.antibioticRequired")}
                  required
                  error={!!validation.errs.antibiotic}
                  helperText={validation.errs.antibiotic}
                />
              )}
            />

            {/* Potency — cascades from antibiotic */}
            <FormControl
              fullWidth
              disabled={!formAntibiotic}
              error={!!validation.errs.potency}
            >
              <InputLabel>{t("customLists.potencyRequired")}</InputLabel>
              <Select
                value={formPotency}
                label={t("customLists.potencyRequired")}
                onChange={(e) => setFormPotency(e.target.value)}
                required
              >
                {potencyOptions.length === 0 ? (
                  <MenuItem disabled value="">
                    <em>{t("customLists.noPotenciesForDrug")}</em>
                  </MenuItem>
                ) : (
                  potencyOptions.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))
                )}
              </Select>
              {validation.errs.potency && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ ml: 2, mt: 0.5 }}
                >
                  {validation.errs.potency}
                </Typography>
              )}
            </FormControl>

            {/* Organism — searchable, selection-only. Species level only for v1. */}
            <Autocomplete
              options={organisms}
              getOptionLabel={(opt) => `${opt.organism} (${opt.whonetOrgCode})`}
              isOptionEqualToValue={(opt, val) =>
                opt.whonetOrgCode === val?.whonetOrgCode
              }
              value={formOrganism}
              onChange={(_, newValue) => {
                setFormOrganism(newValue);
                setFormSite(ANY_INDICATION_LABEL); // cascade reset
              }}
              autoSelect={false}
              autoHighlight
              clearOnBlur
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("customLists.organismRequired")}
                  required
                  error={!!validation.errs.organism}
                  helperText={validation.errs.organism}
                />
              )}
            />

            {/* Host — single dropdown, no Human/Vet filter */}
            <FormControl fullWidth error={!!validation.errs.host}>
              <InputLabel>{t("customLists.hostRequired")}</InputLabel>
              <Select
                value={formHost}
                label={t("customLists.hostRequired")}
                onChange={(e) => {
                  setFormHost(e.target.value);
                  setFormSite(ANY_INDICATION_LABEL); // cascade reset
                }}
                required
              >
                {hosts.map((h) => (
                  <MenuItem key={h.code} value={h.code}>
                    {h.translations?.[i18n.language] || h.displayName}
                  </MenuItem>
                ))}
              </Select>
              {validation.errs.host && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ ml: 2, mt: 0.5 }}
                >
                  {validation.errs.host}
                </Typography>
              )}
            </FormControl>

            {/* Clinical indication — derived from breakpoints, with "General / Not specified" default.
                Note the SEMANTIC: "General / Not specified" here means the
                custom rule applies to ANY clinical indication for this
                drug + organism + host combination — it's a wildcard, not a
                "no site picked yet" placeholder. The helper text below
                makes this explicit when General is the active value. See
                interpretation_algorithm.md Section 11.3. */}
            <FormControl fullWidth>
              <InputLabel>
                {t("customLists.indication")}
                <Tooltip
                  arrow
                  title={t("customLists.indicationTooltip", {
                    label: ANY_INDICATION_LABEL,
                  })}
                >
                  <InfoOutlinedIcon
                    sx={{
                      ml: 0.5,
                      fontSize: "1rem",
                      verticalAlign: "middle",
                      color: "text.secondary",
                    }}
                  />
                </Tooltip>
              </InputLabel>
              <Select
                value={formSite}
                label={t("customLists.indication")}
                onChange={(e) => setFormSite(e.target.value)}
                // Localize the rendered selected value while keeping the
                // underlying value as the English display string (the
                // lookup key for breakpoint matching).
                renderValue={(value) => {
                  if (!value) return "";
                  return clinicalIndicationMap[value] || value;
                }}
              >
                {indicationOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {clinicalIndicationMap[s] || s}
                  </MenuItem>
                ))}
              </Select>
              {formSite === ANY_INDICATION_LABEL && (
                <FormHelperText>
                  {t("customLists.indicationWildcardHelp")}
                </FormHelperText>
              )}
            </FormControl>

            {/* S and R thresholds */}
            <Stack direction="row" spacing={2}>
              <TextField
                label={t("customLists.resistantThreshold")}
                type="number"
                value={formR}
                onChange={(e) => setFormR(e.target.value)}
                fullWidth
                required
                error={!!validation.errs.r}
                helperText={
                  validation.errs.r || t("customLists.zoneDiameterMm")
                }
                inputProps={{ min: MIN_ZONE_MM, max: MAX_ZONE_MM, step: 1 }}
              />
              <TextField
                label={t("customLists.susceptibleThreshold")}
                type="number"
                value={formS}
                onChange={(e) => setFormS(e.target.value)}
                fullWidth
                required
                error={!!validation.errs.s}
                helperText={
                  validation.errs.s || t("customLists.zoneDiameterMm")
                }
                inputProps={{ min: MIN_ZONE_MM, max: MAX_ZONE_MM, step: 1 }}
              />
            </Stack>

            {/* Live preview of derived I range */}
            {formR && formS && !validation.errs.r && !validation.errs.s && (
              <Alert severity="info" sx={{ mt: -1 }}>
                {t("customLists.intermediateRange")}{" "}
                {formatIRange(parseInt(formR, 10), parseInt(formS, 10))}
              </Alert>
            )}

            {/* Notes / rationale */}
            <TextField
              label={t("customLists.notesOptional")}
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText={t("customLists.notesHelperInternal")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            {t("customLists.dialogCancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!validation.isValid || saving}
          >
            {saving
              ? t("customLists.dialogSaving")
              : t("customLists.dialogSave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
